import { useTheme } from '@mui/material';
import { UserDetails } from '@src/store/user/types';
import { useTranslation } from 'react-i18next';

interface BalanceLabelOptions {
    showNegativeStyle?: boolean;
    negative?: boolean;
    allowNegative?: boolean;
}

export const useNumberFormatting = () => {
    const theme = useTheme();
    const { i18n } = useTranslation();

    const formatNumber = (number: number, digits: number = 2) => {
        const locale = i18n.resolvedLanguage;
        const settings = {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits
        };
        return number.toLocaleString('da', settings);
    };

    const toDecimal = (number?: number, digits: number = 0) => {
        if (number !== undefined && number != null) {
            return formatNumber(number, digits);
        } else {
            return number;
        }
    };

    const getCurrencyLabel = (currency: string) => {
        switch (currency) {
            case 'DKK':
                return 'kr.';
            default:
                return currency;
        }
    };

    /**
     * @deprecated This function is deprecated and may be removed in future releases.
     * Formats a numeric balance value as a currency string (or React element) according to Danish locale and currency.
     *
     * @param value - The numeric value to format. Defaults to 0 if undefined.
     * @param currency - The ISO 4217 currency code (e.g., 'DKK').
     * @param negative - If true and value is positive, prepends a negative sign to the formatted string.
     * @param allowNegative - If false, negative values are displayed as 0.
     * @param options - Optional configuration for display, such as showing negative style.
     * @returns The formatted balance as a string or a React element, depending on options.
     */
    const getBalanceLabel = (
        value: number | undefined = 0,
        currency?: string,
        negative = false,
        allowNegative = true,
        options: BalanceLabelOptions | undefined = undefined
    ) => {
        const balanceDisplayAmount =
            value && value > 0 ? value : allowNegative ? value : 0;

        const negativeSign = negative && value > 0 ? '-' : '';

        let formattedBalance = new Intl.NumberFormat('da-DK', {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            roundingMode: 'floor'
        }).format(balanceDisplayAmount);

        formattedBalance = formattedBalance.replace('kr.', 'DKK');

        if (options?.showNegativeStyle) {
            return (
                <span
                    style={{
                        color:
                            value < 0 && allowNegative
                                ? theme.palette.error.main
                                : 'inherit'
                    }}
                >{`${negativeSign}${formattedBalance}`}</span>
            );
        }

        return `${negativeSign}${formattedBalance}`;
    };

    const getBalanceLabelV2 = (
        value: number | undefined = 0,
        currency?: string,
        options?: BalanceLabelOptions
    ) => {
        const mergedOptions: BalanceLabelOptions = {
            showNegativeStyle: false,
            negative: false,
            allowNegative: true,
            ...options
        };

        const balanceDisplayAmount =
            value && value > 0
                ? value
                : mergedOptions.allowNegative
                ? value
                : 0;

        const negativeSign = mergedOptions.negative && value > 0 ? '-' : '';

        let formattedBalance = new Intl.NumberFormat('da-DK', {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            roundingMode: 'floor'
        }).format(balanceDisplayAmount);

        formattedBalance = formattedBalance.replace('kr.', 'DKK');

        return (
            <span
                style={{
                    color:
                        value < 0 &&
                        mergedOptions.allowNegative &&
                        mergedOptions.showNegativeStyle
                            ? theme.palette.error.main
                            : 'inherit'
                }}
            >{`${negativeSign}${formattedBalance}`}</span>
        );
    };

    return {
        toDecimal,
        getCurrencyLabel,
        getBalanceLabelV2,
        getBalanceLabel,
        formatNumber
    };
};
